import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Link from "next/link";
import { useRouter } from "next/navigation";

import { ArrowRight, ChevronLeft, ChevronRight, ExternalLink, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { ColorSelection } from "@/components/product/ColorSelection";
import ImageCarousel from "@/components/product/ImageCarousel";
import { BasicModalShell } from "@/components/shared/BasicModalShell";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDebug } from "@/contexts/DebugContext";
import { useSavedProducts } from "@/contexts/SavedProductsContext";
import { useGlobalAssistantSuppression } from "@/hooks/useGlobalAssistantSuppression";
import { useOutboundRedirectUrl } from "@/hooks/useOutboundRedirectUrl";
import { useProductImageMetadata } from "@/hooks/useProductImageMetadata";
import { buildCanonicalBrandPath } from "@/lib/brand-routing";
import { hasCompleteColorSwatchData } from "@/lib/color-swatch-utils";
import { cacheProductForPDP } from "@/lib/product-store";
import {
  PRODUCT_NAVIGATION_UNAVAILABLE_MESSAGE,
  buildCanonicalProductPathFromRecord,
  getProductBrandSlugFromRecord,
  warnMissingCanonicalProductPath,
} from "@/lib/product-url";
import {
  type Color,
  type Product,
  type ProductPrimaryActionLabels,
  areVariantsSelected,
  getDefaultSelectedColor,
  getDefaultSelectedFit,
  getDefaultSelectedPattern,
  getDefaultSelectedSize,
  getFormattedPrice,
  getProductAvailabilityStatus,
  getProductAnyId,
  getProductCatalogDisplayName,
  getProductImage,
  getProductImages,
  getProductName,
  getProductPrimaryActionLabels,
  getSavedProductId,
} from "@/lib/product-utils";
import { tailwindBreakpoints } from "@/styles/design-tokens";
import { subscribeToMediaQuery } from "@/utils/match-media";

import SaveButton from "./SaveButton";

// Extract DesktopContent to avoid nested component definition
interface DesktopContentProps {
  product: Product;
  savedProductId: string;
  productImages: string[];
  handleImageLoad: (img: { naturalWidth: number; naturalHeight: number }) => void;
  handleImageError: () => void;
  onImageView?: (index: number) => void;
  getBrandName: () => string;
  price: ReturnType<typeof getFormattedPrice>;
  onClose: () => void;
  onMoreLikeThis?: () => void;
  colors: Color[];
  visibleColors: Color[];
  selectedColor: Color | null;
  showColorSwatches: boolean;
  setSelectedColor: (color: Color) => void;
  needsNavigation: boolean;
  canNavigateLeft: boolean;
  canNavigateRight: boolean;
  navigateColors: (direction: "prev" | "next") => void;
  attemptedAdd: boolean;
  missingColor: boolean;
  selectedSize: string;
  setSelectedSize: (size: string) => void;
  missingSize: boolean;
  sellerDisplayName: string;
  availabilityStatusLabel: string | null;
  primaryActionLabel: ProductPrimaryActionLabels;
  handleSeeFullDetails: () => void;
  handleBuyNow: () => void;
  hasBuyNowDestination: boolean;
  isDebug: boolean;
}

function DesktopContent({
  product,
  savedProductId,
  productImages,
  handleImageLoad,
  handleImageError,
  onImageView,
  getBrandName,
  price,
  onClose,
  onMoreLikeThis,
  colors,
  visibleColors,
  selectedColor,
  showColorSwatches,
  setSelectedColor,
  needsNavigation,
  canNavigateLeft,
  canNavigateRight,
  navigateColors,
  attemptedAdd,
  missingColor,
  selectedSize,
  setSelectedSize,
  missingSize,
  sellerDisplayName,
  availabilityStatusLabel,
  primaryActionLabel,
  handleSeeFullDetails,
  handleBuyNow,
  hasBuyNowDestination,
  isDebug,
}: DesktopContentProps) {
  return (
    <div className="flex h-full">
      {/* Image Section - Clean minimal style */}
      <div className="relative flex w-[420px] items-center justify-center bg-gray-50">
        {/* Image Container using ImageCarousel */}
        <div className="relative h-full w-full p-8">
          <ImageCarousel
            images={productImages}
            productName={getProductName(product)}
            objectFit="contain"
            onImageLoad={handleImageLoad}
            onImageError={handleImageError}
            onImageChange={onImageView}
            className="h-full w-full"
          />
        </div>
      </div>

      {/* Content Section */}
      <div className="flex max-w-md flex-1 flex-col p-8">
        <div className="flex-1 space-y-6">
          {/* Header with Icons */}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {/* Brand Name */}
              <p className="mb-2 text-xs font-normal uppercase tracking-[0.15em] text-gray-500">
                <Link
                  href={buildCanonicalBrandPath({
                    brandName: getBrandName(),
                    brandSlug: getProductBrandSlugFromRecord(
                      product as unknown as Record<string, unknown>
                    ),
                  })}
                  className="hover:underline"
                >
                  {getBrandName()}
                </Link>
              </p>
              {/* Product Name */}
              <h2 className="mb-4 text-2xl font-medium leading-tight text-gray-900">
                {getProductName(product)}
              </h2>
              {/* Price */}
              <div className="flex items-baseline gap-3">
                <span className="text-2xl font-semibold text-gray-900">{price.final}</span>
                {price.original && (
                  <span className="text-lg font-normal text-gray-500 line-through">
                    {price.original}
                  </span>
                )}
              </div>
            </div>

            {/* Action Icons - More Like This and Bookmark */}
            <div className="ml-4 flex gap-2">
              {/* More Like This Button */}
              <Button
                variant="ghost"
                size="sm"
                className="p-2 hover:bg-gray-50"
                onClick={() => {
                  onClose();
                  if (onMoreLikeThis) {
                    onMoreLikeThis();
                  }
                }}
              >
                <Sparkles className="h-5 w-5" />
              </Button>

              {/* Save Button */}
              <SaveButton
                productId={savedProductId}
                product={product}
                variant="icon"
                className="!p-2"
              />
            </div>
          </div>

          {/* Color Selection with Navigation Arrows */}
          {colors.length > 0 && (
            <div className="relative">
              {/* Debug: AI Agent Helper for Color Selection */}
              {isDebug && (
                <div className="absolute -top-10 left-0 z-50 rounded bg-orange-600 px-2 py-1 font-mono text-[10px] leading-tight text-white shadow-lg">
                  <div className="font-bold">⚠️ REQUIRED for checkout!</div>
                  <div className="text-[9px]">Click a color button to select</div>
                </div>
              )}
              <ColorSelection
                colors={visibleColors}
                selectedColor={selectedColor}
                showColorSwatches={showColorSwatches}
                showValidationError={attemptedAdd}
                missingColor={missingColor}
                headingClassName="mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-gray-700"
                optionsRowClassName="relative flex items-center"
                optionsClassName="flex flex-1 gap-2"
                textButtonClassName="rounded-md px-4 transition-all"
                selectedTextButtonClassName="border-gray-900 bg-white font-medium text-gray-900"
                unselectedTextButtonClassName="border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                errorMessageClassName="mt-1 text-xs text-red-500"
                optionsAccessory={
                  needsNavigation ? (
                    <>
                      {canNavigateLeft && (
                        <button
                          type="button"
                          aria-label="Show previous colors"
                          className="absolute -left-8 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          onClick={() => navigateColors("prev")}
                        >
                          <ChevronLeft className="h-5 w-5" />
                        </button>
                      )}
                      {canNavigateRight && (
                        <button
                          type="button"
                          aria-label="Show more colors"
                          className="absolute -right-8 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          onClick={() => navigateColors("next")}
                        >
                          <ChevronRight className="h-5 w-5" />
                        </button>
                      )}
                    </>
                  ) : null
                }
                onSelectColor={setSelectedColor}
              />
            </div>
          )}

          {/* Size Selection */}
          {product.sizes && product.sizes.length > 0 && (
            <div className="relative">
              {/* Debug: AI Agent Helper for Size Selection */}
              {isDebug && (
                <div className="absolute -top-10 left-0 z-50 rounded bg-orange-600 px-2 py-1 font-mono text-[10px] leading-tight text-white shadow-lg">
                  <div className="font-bold">⚠️ REQUIRED for checkout!</div>
                  <div className="text-[9px]">Click "Select size" dropdown, then choose a size</div>
                </div>
              )}
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-gray-700">
                SIZE
              </h3>
              <Select
                value={selectedSize}
                onValueChange={(value) => {
                  setSelectedSize(value);
                }}
              >
                <SelectTrigger
                  className={`w-full ${attemptedAdd && missingSize ? "border-red-500" : "border-gray-300"} bg-white py-2.5 text-sm`}
                >
                  <SelectValue placeholder="Select size" />
                </SelectTrigger>
                <SelectContent>
                  {product.sizes.map((size) => (
                    <SelectItem key={size} value={size}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {attemptedAdd && missingSize && (
                <p className="mt-1 text-xs text-red-500">Please select a size</p>
              )}
            </div>
          )}

          {/* Materials Section */}
          {product.materials && product.materials.length > 0 && (
            <div>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-gray-700">
                MATERIALS
              </h3>
              <p className="text-sm text-gray-600">{product.materials.join(", ")}</p>
            </div>
          )}

          {/* Seller Section */}
          {product.url && (
            <div>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-gray-700">
                SELLER
              </h3>
              <p className="text-sm text-gray-600">{sellerDisplayName}</p>
              {availabilityStatusLabel && (
                <p
                  className="mt-2 text-sm font-medium text-amber-700"
                  data-testid="quickview-availability-status"
                >
                  {availabilityStatusLabel}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="relative mt-auto pt-6">
          {/* Debug: AI Agent Helpers */}
          {isDebug && (
            <div className="absolute -top-8 left-0 z-50 flex gap-2">
              <div className="rounded bg-blue-600 px-2 py-1 font-mono text-[10px] leading-tight text-white shadow-lg">
                <div className="font-bold">🤖 "Buy Now"</div>
              </div>
              <div className="rounded bg-purple-600 px-2 py-1 font-mono text-[10px] leading-tight text-white shadow-lg">
                <div className="font-bold">🤖 "Full Details"</div>
              </div>
            </div>
          )}
          <div className="flex gap-3">
            <Button
              size="lg"
              onClick={handleBuyNow}
              aria-label={primaryActionLabel.accessibleLabel}
              className="min-w-0 flex-1 overflow-hidden"
              disabled={!hasBuyNowDestination}
              data-action="buy-now"
              data-testid="quickview-buy-now"
            >
              <span className="min-w-0 truncate">{primaryActionLabel.visibleLabel}</span>
              <ExternalLink aria-hidden="true" className="ml-2 h-4 w-4 flex-shrink-0" />
              <span className="sr-only">(opens in new tab)</span>
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={handleSeeFullDetails}
              className="flex-1"
              data-action="see-full-details"
            >
              Full Details
              <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface QuickviewModalProps {
  product: Product | null;
  isOpen: boolean;
  onClose: () => void;
  onSeeFullDetails?: () => void;
  onMoreLikeThis?: () => void;
  onImageView?: (index: number) => void;
}

export function QuickviewModal({
  product,
  isOpen,
  onClose,
  onSeeFullDetails,
  onMoreLikeThis,
  onImageView,
}: QuickviewModalProps) {
  const [selectedSize, setSelectedSize] = useState<string>("");
  const [selectedColor, setSelectedColor] = useState<Color | null>(null);
  const [selectedPattern, setSelectedPattern] = useState<string | null>(null);
  const [selectedFit, setSelectedFit] = useState<string | null>(null);
  const [attemptedAdd, setAttemptedAdd] = useState(false);

  // Color navigation state
  const [colorStartIndex, setColorStartIndex] = useState(0);
  const [maxVisibleColors, setMaxVisibleColors] = useState(4); // Default for desktop

  // More Like This handled by parent via onMoreLikeThis

  const router = useRouter();
  const { isDebug } = useDebug();
  const { isSaved } = useSavedProducts();
  const didAutoNavigateToPdpRef = useRef(false);

  useGlobalAssistantSuppression(isOpen);

  const productId = getProductAnyId(product);
  const savedProductId = getSavedProductId(product, isSaved);
  const destinationUrl = product?.url ?? null;
  const retailerName = useMemo(() => {
    if (!product?.url) return "RETAILER";

    try {
      const parts = new URL(product.url).hostname.split(".");
      return parts.length >= 2 ? parts[parts.length - 2].toUpperCase() : "RETAILER";
    } catch {
      return "RETAILER";
    }
  }, [product?.url]);
  const { redirectUrl, isLoading: isRedirectUrlLoading } = useOutboundRedirectUrl(destinationUrl, {
    resourceType: "product",
    resourceId: productId,
    merchant: retailerName,
    catalog: product?.catalog ?? null,
  });
  const catalogDisplayName = useMemo(() => getProductCatalogDisplayName(product), [product]);
  const sellerDisplayName = catalogDisplayName || retailerName;
  const availabilityStatus = useMemo(() => getProductAvailabilityStatus(product), [product]);
  const primaryActionLabel = useMemo(
    () =>
      getProductPrimaryActionLabels({
        catalogDisplayName,
        sellerDisplayName,
        isOutOfStock: availabilityStatus.isOutOfStock,
      }),
    [availabilityStatus.isOutOfStock, catalogDisplayName, sellerDisplayName]
  );
  const hasBuyNowDestination = Boolean(destinationUrl) && !isRedirectUrlLoading;

  const productPath = useMemo(
    () =>
      product
        ? buildCanonicalProductPathFromRecord(
            product as unknown as Record<string, unknown>,
            productId
          )
        : null,
    [product, productId]
  );

  // Prefetch the PDP RSC payload as soon as the modal opens so
  // the "Full Details" navigation is effectively instant.
  useEffect(() => {
    if (isOpen && productPath) {
      router.prefetch(productPath);
    }
  }, [isOpen, productPath, router]);

  const handleSeeFullDetails = useCallback(() => {
    onClose();
    if (!product) return;

    if (onSeeFullDetails) {
      onSeeFullDetails();
      return;
    }

    if (!productPath) {
      warnMissingCanonicalProductPath("QuickviewModal", productId);
      toast.error(PRODUCT_NAVIGATION_UNAVAILABLE_MESSAGE);
      return;
    }

    // Store product data for the detail page (lowercased key matches PDP lookup)
    cacheProductForPDP(productId, product);
    router.push(productPath);
  }, [onClose, onSeeFullDetails, product, productId, productPath, router]);

  // Reset the "auto navigate" guard whenever the modal is closed or product changes.
  useEffect(() => {
    if (!isOpen) {
      didAutoNavigateToPdpRef.current = false;
    }
  }, [isOpen, productId]);

  // Quickview is desktop-only. If opened on <md OR the viewport crosses below md while open,
  // navigate to the PDP instead of leaving the user with a closed/hidden modal.
  useEffect(() => {
    if (!isOpen) return;
    if (!product) return;
    if (typeof window === "undefined") return;

    const mql = window.matchMedia(`(min-width: ${tailwindBreakpoints.md}px)`);
    const handle = () => {
      if (mql.matches) return;
      if (didAutoNavigateToPdpRef.current) return;
      didAutoNavigateToPdpRef.current = true;
      handleSeeFullDetails();
    };

    handle();
    const unsub = subscribeToMediaQuery(mql, handle);
    return () => unsub();
  }, [isOpen, product, handleSeeFullDetails]);

  // Memoize product images prioritizing CDN-resolved primary image
  const productImages = useMemo(() => {
    if (!product) return [] as string[];

    const primary = getProductImage(product);
    const others = getProductImages(product);

    const unique = new Set<string>();
    const images: string[] = [];

    if (primary && primary !== "/placeholder.jpg") {
      unique.add(primary);
      images.push(primary);
    }

    for (const url of others) {
      if (!unique.has(url)) {
        unique.add(url);
        images.push(url);
      }
    }

    if (images.length === 0 && primary) {
      images.push(primary);
    }

    return images;
  }, [product]);
  const colors = useMemo(() => product?.color_info?.colors ?? [], [product?.color_info?.colors]);

  // Use the image metadata hook
  const { handleImageLoad } = useProductImageMetadata(product);

  // Mobile detection and max colors calculation
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Use an MQL listener instead of raw resize events; the browser fires this only when the
    // breakpoint boundary is crossed.
    const mql = window.matchMedia(`(min-width: ${tailwindBreakpoints.md}px)`);
    const sync = () => setMaxVisibleColors(mql.matches ? 4 : 3);
    sync();
    const unsub = subscribeToMediaQuery(mql, sync);
    return () => unsub();
  }, []);

  // Reset color navigation when product changes
  useEffect(() => {
    if (product) {
      setColorStartIndex(0);
      setSelectedColor(getDefaultSelectedColor(product));
      setSelectedSize(getDefaultSelectedSize(product) || "");
      setSelectedPattern(getDefaultSelectedPattern(product));
      setSelectedFit(getDefaultSelectedFit(product));
      setAttemptedAdd(false);
    }
  }, [product]);

  const visibleColors = useMemo(
    () => colors.slice(colorStartIndex, colorStartIndex + maxVisibleColors),
    [colors, colorStartIndex, maxVisibleColors]
  );

  if (!product) return null;

  // Color navigation logic
  const totalColors = colors.length;
  const canNavigateLeft = colorStartIndex > 0;
  const canNavigateRight = colorStartIndex + maxVisibleColors < totalColors;
  const needsNavigation = totalColors > maxVisibleColors;
  const showColorSwatches = hasCompleteColorSwatchData(colors);

  const allowMissingSize = true;
  const allowMissingColor = true;
  // Check if variants are selected (size is optional for cart)
  const variantsSelected = areVariantsSelected(
    product,
    {
      color: selectedColor,
      size: selectedSize,
      pattern: selectedPattern,
      fit: selectedFit,
    },
    { allowMissingSize, allowMissingColor }
  );

  // Missing field helpers for inline validation (shown after an attempt)
  const missingColor = !allowMissingColor && colors.length > 0 && !selectedColor;
  const missingSize = !allowMissingSize && (product.sizes?.length || 0) > 0 && !selectedSize;

  const navigateColors = (direction: "prev" | "next") => {
    if (direction === "prev" && canNavigateLeft) {
      setColorStartIndex(Math.max(0, colorStartIndex - 1));
    } else if (direction === "next" && canNavigateRight) {
      setColorStartIndex(Math.min(totalColors - maxVisibleColors, colorStartIndex + 1));
    }
  };

  const openRetailer = () => {
    if (isRedirectUrlLoading) {
      return;
    }

    const outboundUrl = redirectUrl || destinationUrl;
    if (!outboundUrl) {
      toast.error("Retailer link unavailable");
      return;
    }
    const openedWindow = window.open(outboundUrl, "_blank", "noopener,noreferrer");
    if (!openedWindow) {
      toast.error("Couldn't open retailer site. Please allow pop-ups and try again.");
    }
  };

  const handleBuyNow = (skipVariantValidation = false) => {
    if (skipVariantValidation) {
      openRetailer();
      return;
    }

    setAttemptedAdd(true);

    const isMissingColor = !allowMissingColor && colors.length > 0 && !selectedColor;
    const isMissingSize = !allowMissingSize && (product.sizes?.length || 0) > 0 && !selectedSize;

    if (isMissingColor || isMissingSize) {
      return;
    }

    if (!variantsSelected) {
      toast.error("Please select all required options.");
      return;
    }

    openRetailer();
  };

  // Get brand name
  const getBrandName = (): string => {
    // Prefer enrichment canonical brand name if available
    if (product.enrichment?.canonical_brand?.brand_name) {
      return product.enrichment.canonical_brand.brand_name;
    }
    if (product.brand_name) return product.brand_name;
    if (product.catalog)
      return product.catalog.charAt(0).toUpperCase() + product.catalog.slice(1).toLowerCase();
    return "Brand";
  };

  // Get price
  const price = getFormattedPrice(product);

  const handleImageError = () => {
    console.warn(`Image failed to load for product: ${getProductName(product)}`);
  };

  return (
    <>
      <BasicModalShell
        isOpen={isOpen}
        onClose={onClose}
        panelClassName="md:max-w-[980px] md:max-h-[80vh]"
        scrollClassName="overflow-hidden items-stretch"
      >
        <DesktopContent
          product={product}
          savedProductId={savedProductId}
          productImages={productImages}
          handleImageLoad={handleImageLoad}
          handleImageError={handleImageError}
          onImageView={onImageView}
          getBrandName={getBrandName}
          price={price}
          onClose={onClose}
          onMoreLikeThis={onMoreLikeThis}
          colors={colors}
          visibleColors={visibleColors}
          selectedColor={selectedColor}
          showColorSwatches={showColorSwatches}
          setSelectedColor={setSelectedColor}
          needsNavigation={needsNavigation}
          canNavigateLeft={canNavigateLeft}
          canNavigateRight={canNavigateRight}
          navigateColors={navigateColors}
          attemptedAdd={attemptedAdd}
          missingColor={missingColor}
          selectedSize={selectedSize}
          setSelectedSize={setSelectedSize}
          missingSize={missingSize}
          sellerDisplayName={sellerDisplayName}
          availabilityStatusLabel={availabilityStatus.statusLabel}
          primaryActionLabel={primaryActionLabel}
          handleSeeFullDetails={handleSeeFullDetails}
          handleBuyNow={() => handleBuyNow(availabilityStatus.isOutOfStock)}
          hasBuyNowDestination={hasBuyNowDestination}
          isDebug={isDebug}
        />
      </BasicModalShell>
    </>
  );
}
